import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clock, MapPin } from 'lucide-react';
import SafeImage from './SafeImage';
import OfferRibbon, { useOfferCountdown } from './OfferRibbon';
import PriceTag from './PriceTag';
import WhatsAppShareBtn from './WhatsAppShareBtn';

const Card = ({ p, showPrice }) => {
  // Live-updating "Xd Yh Zm" string used to render the small offer-ends line
  // right below the discounted price.
  const countdown = useOfferCountdown(p.offer_ends_at);

  return (
    <Link
      to={`/package/${p.pkg_code || p.id}`}
      data-card
      className="snap-start shrink-0 w-[300px] md:w-[340px] bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition group"
    >
      <div className="relative overflow-hidden">
        <SafeImage
          src={p.image}
          alt={p.title}
          fallbackType="beach"
          className="w-full h-56 object-cover group-hover:scale-105 transition duration-500"
        />
        {p.offer_enabled && Number(p.offer_price) > 0 && Number(p.offer_price) < Number(p.price || 0) && (
          <OfferRibbon
            text={p.offer_ribbon_text || 'Limited Offer'}
            endsAt={p.offer_ends_at}
            variant="card"
          />
        )}
        {(() => {
          const themeRaw = Array.isArray(p.themes) && p.themes.length > 0
            ? p.themes[0]
            : (p.category === 'india' ? 'India' : 'International');
          const label = String(themeRaw).replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
          return (
            <div className="absolute top-3 right-3 bg-[#0d64a8] text-white text-[11px] font-medium px-2.5 py-1 rounded shadow-sm capitalize">
              {label}
            </div>
          );
        })()}
      </div>

      <div className="pt-5 px-5 pb-5">
        <h3 className="text-[17px] font-semibold text-gray-900 mb-2 line-clamp-2 min-h-[48px]">
          {p.title}
        </h3>
        <div className="flex items-center gap-3 text-xs text-gray-600 mb-3">
          {p.duration && (
            <span className="inline-flex items-center gap-1 text-gray-700" data-testid="pkg-duration">
              <Clock size={12} className="text-[#0d64a8]" /> {p.duration}
            </span>
          )}
          <span className="flex items-start gap-1 min-w-0">
            <MapPin size={12} className="text-[#0d64a8] mt-0.5 shrink-0" />
            <span className="line-clamp-1">{p.itinerary}</span>
          </span>
        </div>
        <div className="h-px bg-gray-100 my-3" />
        {showPrice && (
          <div className="flex items-end justify-between gap-2">
            <PriceTag
              price={p.price}
              offerEnabled={p.offer_enabled}
              offerPrice={p.offer_price}
              size="md"
              suffix="/ Adult"
              offerEndsIn={countdown}
            />
            <div className="flex items-center gap-2 shrink-0">
              <WhatsAppShareBtn pkg={p} variant="card" />
              <span className="bg-[#0d64a8] hover:bg-[#0a548e] text-white text-xs font-medium px-3 py-2 rounded transition">
                View Details
              </span>
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};

const PackageCarousel = ({ items, showPrice = true }) => {
  const scrollerRef = useRef(null);

  const scroll = (dir) => {
    if (!scrollerRef.current) return;
    const el = scrollerRef.current;
    const card = el.querySelector('[data-card]');
    const step = card ? card.offsetWidth + 20 : 320;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      <button
        aria-label="prev"
        onClick={() => scroll(-1)}
        className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0d64a8] text-white items-center justify-center shadow-lg hover:bg-[#0a548e] transition"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        aria-label="next"
        onClick={() => scroll(1)}
        className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#0d64a8] text-white items-center justify-center shadow-lg hover:bg-[#0a548e] transition"
      >
        <ChevronRight size={20} />
      </button>

      <div
        ref={scrollerRef}
        className="flex gap-5 overflow-x-auto scroll-smooth snap-x pb-4 -mx-1 px-1"
        style={{ scrollbarWidth: 'none' }}
      >
        {items.map((p) => (
          <Card key={p.id} p={p} showPrice={showPrice} />
        ))}
      </div>
    </div>
  );
};

export default PackageCarousel;
